/**
 * Typed wrappers around the /api/v1/auth/* + /api/v1/me endpoints.
 * Paths and shapes are taken verbatim from backend/internal/router and
 * backend/internal/handlers/auth.go.
 */

import { api, clearCSRFToken, setCSRFToken, type RetriableConfig } from "./client";
import {
  isSelectWorkspace,
  type ActivateRequest,
  type ForgotPasswordRequest,
  type ForgotPasswordResponse,
  type LoginRequest,
  type LoginResponse,
  type LoginResult,
  type MeResponse,
  type MyWorkspacesResponse,
  type RegisterRequest,
  type RegisterResponse,
  type ResetPasswordRequest,
  type TOTPDisableRequest,
  type TOTPSetupResponse,
  type TOTPVerifyRequest,
} from "@/types/auth";

// POST /api/v1/auth/register — 201 (OptionalAuth).
//
// ID.2: register auto-activates the just-created company and returns a
// FULL tenant-scoped loginResponse + session cookies (may be
// totp_required). With an identity session cookie present it ATTACHES
// a new company to that identity ("add another company"); a new email
// creates a fresh identity. An existing email with no/foreign session
// → 409 identity_exists (handled by the caller).
export async function register(
  body: RegisterRequest,
): Promise<RegisterResponse> {
  const { data } = await api.post<RegisterResponse>(
    "/api/v1/auth/register",
    body,
  );
  // Cross-origin staging fallback: backend's wr_csrf cookie is host-only
  // so document.cookie can't see it; stash the token from the body
  // (data.csrf_token) into the in-memory store so subsequent writes
  // include the X-CSRF-Token header. No-op on a same-origin deploy
  // where the cookie itself is readable.
  setCSRFToken(data.csrf_token);
  return data;
}

// POST /api/v1/auth/login — 200. Identity auth: email + password only
// (NO company_slug).
//
// Two 200 shapes (see backend auth.go):
//  - normal: loginResponse + Set-Cookie (wr_access/wr_refresh/wr_csrf),
//    possibly totp_required. Single membership OR platform identity.
//  - { select_workspace:true, workspaces, totp_required } + an
//    IDENTITY-ONLY session cookie (no active tenant), when the identity
//    has >=2 active memberships. The caller opens the picker and POSTs
//    the chosen slug to /auth/activate — no selection token.
// `isSelectWorkspace()` (types/auth) is the discriminant.
export async function login(body: LoginRequest): Promise<LoginResult> {
  const { data } = await api.post<LoginResult>("/api/v1/auth/login", body);
  // Stash the CSRF token from the body on the LoginResponse branch
  // (the picker / SelectWorkspaceResponse branch has no body CSRF
  // because no tenant is active yet — /activate writes it). See
  // setCSRFToken docstring for the cross-origin rationale.
  if (!isSelectWorkspace(data)) {
    setCSRFToken(data.csrf_token);
  }
  return data;
}

// POST /api/v1/auth/activate — 200 (authed: identity-only OR active
// session; CSRF-exempt). THE single endpoint that establishes/switches
// the active tenant: the post-login picker target AND the in-app
// instant switch (no password, no logout). On success returns the
// IDENTICAL loginResponse + full (rotated) tenant cookies as a normal
// login. Errors: 403 not_a_member | platform_identity |
// account_inactive (all generic, no enumeration).
export async function activate(
  body: ActivateRequest,
): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>(
    "/api/v1/auth/activate",
    body,
  );
  // /activate rotates cookies + returns a fresh csrf_token in the body —
  // re-stash for the cross-origin staging fallback (same logic as login
  // + register; see setCSRFToken docstring).
  setCSRFToken(data.csrf_token);
  return data;
}

// GET /api/v1/auth/my-workspaces — 200 (auth required, GET → no CSRF).
// Identity-scoped: the caller's current workspace (if an active tenant)
// plus their OTHER memberships. Drives both the in-app switcher and the
// identity-only "pick a workspace" state.
export async function getMyWorkspaces(): Promise<MyWorkspacesResponse> {
  const { data } = await api.get<MyWorkspacesResponse>(
    "/api/v1/auth/my-workspaces",
  );
  return data;
}

// POST /api/v1/auth/logout — 200 (auth + CSRF required)
export async function logout(): Promise<void> {
  await api.post("/api/v1/auth/logout");
  // Clear the in-memory CSRF too so the next anonymous request doesn't
  // attempt to use a now-revoked token. The cookie is cleared by the
  // backend's Set-Cookie; this is the cross-origin counterpart.
  clearCSRFToken();
}

// GET /api/v1/me — 200 (auth required, no CSRF — safe method)
//
// `probe: true` is for the mount-time anonymous check: it sets
// _suppressAuthRedirect so a 401 rejects quietly (no refresh, no
// /login bounce) — letting AuthProvider set user=null for visitors on
// public pages like /register or /forgot.
export async function getMe(opts?: { probe?: boolean }): Promise<MeResponse> {
  const { data } = await api.get<MeResponse>("/api/v1/me", {
    _suppressAuthRedirect: opts?.probe === true,
  } as RetriableConfig);
  return data;
}

// POST /api/v1/auth/forgot-password — 200 (always success-shaped)
export async function forgotPassword(
  body: ForgotPasswordRequest,
): Promise<ForgotPasswordResponse> {
  const { data } = await api.post<ForgotPasswordResponse>(
    "/api/v1/auth/forgot-password",
    body,
  );
  return data;
}

// POST /api/v1/auth/reset-password — 200
export async function resetPassword(
  body: ResetPasswordRequest,
): Promise<void> {
  await api.post("/api/v1/auth/reset-password", body);
}

// POST /api/v1/auth/totp/setup — 200 (auth + CSRF required)
export async function totpSetup(): Promise<TOTPSetupResponse> {
  const { data } = await api.post<TOTPSetupResponse>(
    "/api/v1/auth/totp/setup",
  );
  return data;
}

// POST /api/v1/auth/totp/verify — 200 (auth + CSRF required)
// On success the backend re-issues wr_access with the totp=true claim.
export async function totpVerify(body: TOTPVerifyRequest): Promise<void> {
  await api.post("/api/v1/auth/totp/verify", body);
}

// POST /api/v1/auth/totp/disable — 200 (auth + CSRF required)
export async function totpDisable(body: TOTPDisableRequest): Promise<void> {
  await api.post("/api/v1/auth/totp/disable", body);
}

// POST /api/v1/auth/dpdp-consent — 200 (auth + CSRF required; no body).
// Records the per-membership DPDP 2023 employee consent. The backend
// stores `EMPLOYEE_DPDP_CONSENT_PURPOSE` verbatim into
// `users.consent_purpose`; the response echoes that same string so the
// caller can sanity-check the lockstep. Fails closed for identity-only
// / platform sessions (no active membership ⇒ 403 tenant_required).
export async function dpdpConsent(): Promise<{ status: string; purpose: string }> {
  const { data } = await api.post<{ status: string; purpose: string }>(
    "/api/v1/auth/dpdp-consent",
  );
  return data;
}
